/** Periods and year-end (M03-07, M03-09): lock in order, unlock with a reason. */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, LockOpen, ShieldCheck } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { shortDate } from '../../lib/format';
import { useFiscalYears, type FiscalYear, type Period } from '../../lib/queries';
import { ReasonDialog } from '../../components/ReasonDialog';
import { Alert, Button, Card, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th } from '../../components/ui';

const TONE = { OPEN: 'ok', SOFT_LOCKED: 'warning', HARD_LOCKED: 'critical' } as const;
const LABEL = { OPEN: 'Open', SOFT_LOCKED: 'Soft-locked', HARD_LOCKED: 'Hard-locked' } as const;

export function PeriodsPage(): JSX.Element {
  const { can } = useAuth();
  const years = useFiscalYears();
  const queryClient = useQueryClient();
  const [unlocking, setUnlocking] = useState<{ period: Period; to: Period['status'] } | null>(null);
  const [closing, setClosing] = useState<FiscalYear | null>(null);
  const [error, setError] = useState<string | null>(null);
  const manage = can('periods.manage');

  const done = (): void => {
    setError(null);
    setUnlocking(null);
    setClosing(null);
    void queryClient.invalidateQueries({ queryKey: ['fiscal-years'] });
  };
  const fail = (caught: unknown): void => setError(caught instanceof ApiError ? caught.message : 'That did not work.');

  const setStatus = useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: Period['status']; reason?: string }) =>
      api(`/fiscal-years/periods/${id}/status`, { method: 'POST', body: { status, reason } }),
    onSuccess: done,
    onError: fail,
  });
  const nextYear = useMutation({ mutationFn: () => api('/fiscal-years/next', { method: 'POST' }), onSuccess: done, onError: fail });
  const closeYear = useMutation({ mutationFn: (id: string) => api(`/fiscal-years/${id}/close`, { method: 'POST' }), onSuccess: done, onError: fail });

  return (
    <>
      <PageHeader
        title="Periods & Year-end"
        description="Soft-lock a month for review (only Accountants can post), then hard-lock it (no one can). Lock in order; unlocking needs a reason."
        actions={manage && <Button variant="secondary" busy={nextYear.isPending} onClick={() => nextYear.mutate()}>Open next fiscal year</Button>}
      />
      {error && !unlocking && !closing && <div className="mb-4"><Alert tone="critical">{error}</Alert></div>}
      {years.isLoading && <Skeleton className="h-96 w-full" />}
      <div className="space-y-4">
        {years.data?.map((y) => (
          <Card
            key={y.id}
            title={y.name}
            description={`${shortDate(y.startDate)} – ${shortDate(y.endDate)}`}
            actions={
              y.status === 'CLOSED' ? (
                <StatusChip tone="neutral"><ShieldCheck aria-hidden="true" className="h-3.5 w-3.5" /> Closed</StatusChip>
              ) : (
                manage && <Button size="sm" variant="secondary" onClick={() => setClosing(y)}>Close the year</Button>
              )
            }
          >
            <TableWrap>
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <Th>Period</Th>
                  <Th>Dates</Th>
                  <Th>Status</Th>
                  <Th>{''}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {y.periods.map((p) => (
                  <tr key={p.id}>
                    <Td className="font-medium">{p.name}</Td>
                    <Td className="whitespace-nowrap text-slate-500">{shortDate(p.startDate)} – {shortDate(p.endDate)}</Td>
                    <Td><StatusChip tone={TONE[p.status]}>{LABEL[p.status]}</StatusChip></Td>
                    <Td align="right">
                      {manage && y.status === 'OPEN' && (
                        <div className="flex justify-end gap-1">
                          {p.status === 'OPEN' && (
                            <Button size="sm" variant="secondary" busy={setStatus.isPending} onClick={() => setStatus.mutate({ id: p.id, status: 'SOFT_LOCKED' })}>
                              <Lock aria-hidden="true" className="h-3.5 w-3.5" /> Soft-lock
                            </Button>
                          )}
                          {p.status === 'SOFT_LOCKED' && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => setUnlocking({ period: p, to: 'OPEN' })}>
                                <LockOpen aria-hidden="true" className="h-3.5 w-3.5" /> Reopen
                              </Button>
                              <Button size="sm" variant="secondary" busy={setStatus.isPending} onClick={() => setStatus.mutate({ id: p.id, status: 'HARD_LOCKED' })}>
                                <Lock aria-hidden="true" className="h-3.5 w-3.5" /> Hard-lock
                              </Button>
                            </>
                          )}
                          {p.status === 'HARD_LOCKED' && (
                            <Button size="sm" variant="ghost" onClick={() => setUnlocking({ period: p, to: 'SOFT_LOCKED' })}>
                              <LockOpen aria-hidden="true" className="h-3.5 w-3.5" /> Unlock
                            </Button>
                          )}
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </Card>
        ))}
      </div>
      {unlocking && (
        <ReasonDialog
          title={`Unlock ${unlocking.period.name}?`}
          description="Unlocking is recorded in the audit log with your reason."
          confirmLabel="Unlock"
          tone="primary"
          error={error}
          busy={setStatus.isPending}
          onClose={() => { setUnlocking(null); setError(null); }}
          onConfirm={(reason) => setStatus.mutate({ id: unlocking.period.id, status: unlocking.to, reason })}
        />
      )}
      {closing && (
        <Modal title={`Close ${closing.name}?`} description="Income and expense accounts are closed to Retained Earnings, and every period is hard-locked for good. This cannot be undone." onClose={() => { setClosing(null); setError(null); }}>
          {error && <div className="mb-3"><Alert tone="critical">{error}</Alert></div>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setClosing(null)}>Cancel</Button>
            <Button variant="danger" busy={closeYear.isPending} onClick={() => closeYear.mutate(closing.id)}>Close the year</Button>
          </div>
        </Modal>
      )}
    </>
  );
}
